import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import T from "./styles/tokens";
import Sidebar from "./components/Sidebar";
import AuthGate from "./auth/AuthGate";

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

// Admin pages
import Dashboard from "./pages/Dashboard";
import { ClientsList, ClientDetail } from "./pages/Clients";
import NewInvitation from "./pages/NewInvitation";
import AuditLog from "./pages/AuditLog";
import TenantHealth from "./pages/TenantHealth";
import ClientManagement from "./pages/ClientManagement";

// Public pages (no sidebar)
import ClientLanding from "./pages/ClientLanding";
import OffboardingLanding from "./pages/OffboardingLanding";
import Confirmation from "./pages/Confirmation";
import ErrorPage from "./pages/ErrorPage";

/**
 * Layout wrapper for admin pages — includes sidebar and content area.
 */
function AdminLayout() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  // Open by default on desktop, closed on mobile. Reflect changes when the
  // viewport crosses the breakpoint.
  useEffect(() => setSidebarOpen(!isMobile), [isMobile]);
  // Close on navigation when on mobile.
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {isMobile && sidebarOpen && (
        <div className="pf-sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}
      {isMobile && (
        <button
          aria-label="Open menu"
          className="pf-sidebar-toggle"
          onClick={() => setSidebarOpen((o) => !o)}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M3 5h14M3 10h14M3 15h14" />
          </svg>
        </button>
      )}
      <main
        className="pf-admin-main"
        style={{
          flex: 1,
          marginLeft: isMobile ? 0 : 220,
          padding: isMobile ? "56px 16px 24px" : "28px 36px",
          background: T.gray100,
          minHeight: "100vh",
          fontFamily: T.font,
        }}
      >
        <Outlet />
      </main>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* ─── Public routes (no sidebar, full-screen) ─────────────── */}
        <Route path="/invite/:token" element={<ClientLanding />} />
        <Route path="/offboard/:token" element={<OffboardingLanding />} />
        <Route path="/confirmation" element={<Confirmation />} />
        <Route path="/error" element={<ErrorPage />} />

        {/* ─── Admin routes (sidebar layout) ───────────────────────── */}
        <Route path="/dashboard" element={<AuthGate><AdminLayout /></AuthGate>}>
          <Route index element={<Dashboard />} />
          <Route path="clients" element={<ClientsList />} />
          <Route path="clients/:id" element={<ClientDetail />} />
          <Route path="clients/:id/health" element={<TenantHealth />} />
          <Route path="clients/:id/manage" element={<ClientManagement />} />
          <Route path="invite" element={<NewInvitation />} />
          <Route path="audit" element={<AuditLog />} />
        </Route>

        {/* ─── Fallback ────────────────────────────────────────────── */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
